import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TablaIcon from '../components/TablaIcon';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { login } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...s.page, flexDirection: isMobile ? 'column' : 'row' }}>

      {/* Left panel — hidden on mobile */}
      {!isMobile && (
        <div style={s.left}>
          <div style={s.leftContent}>
            <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
              <TablaIcon size={72} color="#c49a3c"/>
            </div>
            <h1 style={{ ...s.brandName, cursor: "pointer" }} onClick={() => navigate("/")}>
              RhythMicky<br/>Beats
            </h1>
            <p style={s.brandSub}>Music Institute Portal</p>
            <div style={s.rule}><div style={s.ruleBar}/></div>
            <div style={s.featureList}>
              {['Classes & Lessons','Student Progress','Homework (Ryaaz)','Schedule & Attendance'].map(f => (
                <div key={f} style={s.featureItem}>
                  <div style={s.featureDot}/>
                  <span style={s.featureText}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <p style={s.copyright}>© {new Date().getFullYear()} RhythMicky Beats Institute</p>
        </div>
      )}

      {/* Right form panel */}
      <div style={{ ...s.right, padding: isMobile ? '32px 20px' : '40px' }}>

        {/* Show logo on mobile */}
        {isMobile && (
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div onClick={() => navigate("/")} style={{ cursor: 'pointer', display: 'inline-block', marginBottom: '12px' }}>
              <TablaIcon size={56} color="#c49a3c"/>
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 300, color: '#f0ede8',
              letterSpacing: '-1px', fontFamily: "'DM Serif Display',Georgia,serif",
              cursor: 'pointer' }} onClick={() => navigate("/")}>
              RhythMicky Beats
            </h1>
            <p style={{ fontSize: '11px', color: '#6b6966', letterSpacing: '1.5px',
              textTransform: 'uppercase', marginTop: '4px' }}>Music Institute Portal</p>
          </div>
        )}

        <div style={{ ...s.card, width: isMobile ? '100%' : undefined }}>
          <div style={s.cardTop}>
            <h2 style={s.cardTitle}>Sign In</h2>
            <p style={s.cardSub}>Enter your credentials to access the portal</p>
          </div>

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Username</label>
              <input style={s.input} type="text" value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="your username" autoComplete="username" required/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input style={s.input} type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" required/>
            </div>

            {error && <div style={s.error}>{error}</div>}
            <div style={{ textAlign: 'right', marginBottom: '4px' }}>
              <a href="/forgot-password" style={{ fontSize: '12px', color: '#c49a3c', textDecoration: 'none' }}>
                Forgot password or username?
              </a>
            </div>

            <button type="submit"
              style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
              disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          {isMobile && (
            <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b6966', marginTop: '20px' }}>
              Don't have an account?{' '}
              <button onClick={() => navigate('/signup')}
                style={{ background: 'none', border: 'none', color: '#c49a3c',
                  cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                  fontFamily: "'DM Sans',sans-serif", padding: 0 }}>
                Apply for access
              </button>
            </p>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'DM Sans','Helvetica Neue',sans-serif; }
        input:focus { outline:none; border-color:#c49a3c !important; box-shadow:0 0 0 3px rgba(196,154,60,0.15) !important; }
        input::placeholder { color:#4a4845; }
      `}</style>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', display:'flex', fontFamily:"'DM Sans','Helvetica Neue',sans-serif", background:'#0e0e0d' },
  left: { width:'400px', flexShrink:0, background:'#111110', borderRight:'1px solid #2a2927', display:'flex', flexDirection:'column', padding:'48px 40px' },
  leftContent: { flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:'22px' },
  brandName: { fontSize:'40px', fontWeight:300, lineHeight:1.1, color:'#f0ede8', letterSpacing:'-1.5px', fontFamily:"'DM Serif Display',Georgia,serif" },
  brandSub: { fontSize:'12px', color:'#6b6966', letterSpacing:'1.5px', textTransform:'uppercase' },
  rule: { display:'flex', alignItems:'center', gap:'12px' },
  ruleBar: { width:'28px', height:'1px', background:'#c49a3c', flexShrink:0 },
  featureList: { display:'flex', flexDirection:'column', gap:'10px', marginTop:'4px' },
  featureItem: { display:'flex', alignItems:'center', gap:'10px' },
  featureDot: { width:'5px', height:'5px', borderRadius:'50%', background:'#2a2927', flexShrink:0 },
  featureText: { fontSize:'13px', color:'#6b6966' },
  copyright: { fontSize:'11px', color:'#3a3835' },
  right: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#0e0e0d' },
  card: { width:'100%', maxWidth:'400px', background:'#161614', borderRadius:'16px', border:'1px solid #2a2927', padding:'36px', boxShadow:'0 4px 24px rgba(0,0,0,0.4)' },
  cardTop: { marginBottom:'28px' },
  cardTitle: { fontSize:'22px', fontWeight:600, color:'#f0ede8', letterSpacing:'-0.4px', marginBottom:'6px' },
  cardSub: { fontSize:'13px', color:'#6b6966' },
  form: { display:'flex', flexDirection:'column', gap:'20px' },
  field: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { fontSize:'11px', fontWeight:600, color:'#6b6966', letterSpacing:'0.6px', textTransform:'uppercase' },
  input: { padding:'11px 14px', borderRadius:'8px', border:'1px solid #2a2927', fontSize:'14px', color:'#f0ede8', background:'#1c1b19', outline:'none', fontFamily:"'DM Sans',sans-serif" },
  error: { padding:'10px 14px', borderRadius:'8px', background:'rgba(224,82,82,0.1)', border:'1px solid rgba(224,82,82,0.25)', fontSize:'13px', color:'#e05252' },
  btn: { padding:'13px', borderRadius:'8px', border:'none', background:'#c49a3c', color:'#0e0e0d', fontSize:'14px', fontWeight:600, cursor:'pointer', letterSpacing:'0.2px', fontFamily:"'DM Sans',sans-serif", marginTop:'4px' },
  btnDisabled: { background:'#4a4845', color:'#8a8784', cursor:'not-allowed' },
};
